/**
 * Roles + permission matrix editor. Platform users with roles.internal.manage
 * or roles.merchant.manage can see and edit; superuser sees everything.
 *
 * Layout:
 *  - Left: list of roles, grouped by scope (internal | merchant). Click to select.
 *  - Right: detail panel — role name, description, system flag, and a feature
 *    matrix grouped by category. Each row is a feature with Create / View /
 *    Edit / Suspend cells, plus an Extras column for non-CRUD permissions
 *    (retry, mark-dead, ledger view, etc.). Save commits the new set.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Grid,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconAlertCircle,
  IconCircleCheck,
  IconCircleDashed,
  IconLock,
  IconPlus,
} from '@tabler/icons-react';
import { rolesApi, type PermissionDto, type RoleDto, type RoleScope } from '../api/roles';
import { ApiCallError } from '../api/client';
import { useProfile, usePermissions } from '../auth/SessionContext';
import { isPlatform } from '../auth/permissions';
import { useTranslation } from '../i18n/LocaleContext';
import type { TranslationKey } from '../i18n/translations';

/**
 * Modules visible to each role scope.
 *  - internal roles can hold `admin` + `shared` permissions
 *  - merchant roles can hold `merchant` + `shared` permissions
 *
 * We filter individual permission cells against this so an internal role's
 * editor never shows merchant-only keys, and vice versa. The server still
 * rejects mismatches if a stale client tries to PATCH them.
 */
const MODULES_BY_SCOPE: Record<RoleScope, readonly string[]> = {
  internal: ['admin', 'shared'],
  merchant: ['merchant', 'shared'],
};

type FeatureAction = 'create' | 'view' | 'edit' | 'suspend';

type FeatureExtra = { key: string; labelKey: TranslationKey };

/**
 * Who actually has the feature in production:
 *  - `internal`: TransPay staff (e.g. merchant onboarding, internal user mgmt)
 *  - `merchant`: merchant + sub-users under the merchant
 *  - `both`:   shared surface (overview, deposit list, IP whitelist for login)
 */
type FeatureAppliesTo = 'internal' | 'merchant' | 'both';

type FeatureDef = {
  id: string;
  labelKey: TranslationKey;
  descriptionKey?: TranslationKey;
  appliesTo: FeatureAppliesTo;
  actions: Partial<Record<FeatureAction, string>>;
  extras?: readonly FeatureExtra[];
};

const APPLIES_TO_META: Record<FeatureAppliesTo, { labelKey: TranslationKey; color: string }> = {
  internal: { labelKey: 'roles.appliesInternal', color: 'red' },
  merchant: { labelKey: 'roles.appliesMerchant', color: 'brand' },
  both: { labelKey: 'roles.appliesBoth', color: 'blue' },
};

type CategoryDef = {
  id: string;
  labelKey: TranslationKey;
  hintKey?: TranslationKey;
  color: string;
  features: readonly FeatureDef[];
};

const ACTION_COLUMNS: ReadonlyArray<{
  id: FeatureAction;
  labelKey: TranslationKey;
  color: string;
}> = [
  { id: 'create', labelKey: 'roles.colCreate', color: 'green' },
  { id: 'view', labelKey: 'roles.colView', color: 'blue' },
  { id: 'edit', labelKey: 'roles.colEdit', color: 'yellow' },
  { id: 'suspend', labelKey: 'roles.colSuspend', color: 'red' },
];

/**
 * Catalog mapping each conceptual feature to its underlying permission keys.
 * The shape (Create/View/Edit/Suspend + Extras) lets the editor render a
 * tidy 4-column CRUD grid; the wire format on the server is still flat
 * permission keys, so anything outside CRUD lives in `extras`.
 */
const FEATURE_CATALOG: readonly CategoryDef[] = [
  {
    id: 'business',
    labelKey: 'roles.catBusiness',
    hintKey: 'roles.catBusinessHint',
    color: 'blue',
    features: [
      {
        id: 'overview',
        labelKey: 'roles.featOverview',
        appliesTo: 'both',
        actions: { view: 'overview.access' },
        extras: [
          { key: 'overview.balances.view', labelKey: 'roles.extraViewBalances' },
          { key: 'overview.counts.view', labelKey: 'roles.extraViewTrxCount' },
          { key: 'overview.charts.view', labelKey: 'roles.extraViewCharts' },
        ],
      },
      {
        id: 'deposits',
        labelKey: 'roles.featDeposits',
        appliesTo: 'both',
        actions: { view: 'deposits.list.view' },
        extras: [
          { key: 'deposits.detail.view', labelKey: 'roles.extraViewDetail' },
          // admin-module → hidden by isInScope on merchant roles.
          { key: 'deposits.status.update', labelKey: 'roles.extraUpdateStatus' },
        ],
      },
      {
        id: 'disbursements',
        labelKey: 'roles.featDisbursements',
        appliesTo: 'both',
        descriptionKey: 'roles.featDisbursementsDesc',
        actions: {
          create: 'disbursements.create',
          view: 'disbursements.list.view',
        },
        extras: [
          { key: 'disbursements.detail.view', labelKey: 'roles.extraViewDetail' },
          { key: 'disbursements.status.update', labelKey: 'roles.extraUpdateStatus' },
        ],
      },
      {
        id: 'transaction_report',
        labelKey: 'roles.featTransactionReport',
        descriptionKey: 'roles.featTransactionReportDesc',
        appliesTo: 'both',
        actions: { view: 'report.transaction.view' },
        extras: [
          {
            key: 'report.transaction.download',
            labelKey: 'roles.extraDownloadTransactionReport',
          },
          {
            key: 'report.transaction.view.all',
            labelKey: 'roles.extraViewAllMerchantReports',
          },
        ],
      },
      {
        // Page-access permission for the personalised API Documentation page.
        // Module 'shared' → grantable to both internal and merchant roles.
        id: 'api_docs',
        labelKey: 'roles.featApiDocs',
        descriptionKey: 'roles.featApiDocsDesc',
        appliesTo: 'both',
        actions: { view: 'apidocs.view' },
      },
    ],
  },
  {
    id: 'merchant_ops',
    labelKey: 'roles.catMerchantOps',
    hintKey: 'roles.catMerchantOpsHint',
    color: 'brand',
    features: [
      {
        id: 'merchants',
        labelKey: 'roles.featMerchants',
        appliesTo: 'internal',
        actions: {
          create: 'merchants.create',
          view: 'merchants.list.view',
          edit: 'merchants.edit',
          suspend: 'merchants.suspend',
        },
        extras: [
          { key: 'merchants.credentials.manage', labelKey: 'roles.extraManageCredentials' },
          { key: 'merchants.webhook.manage', labelKey: 'roles.extraManageWebhookUrl' },
          { key: 'platform.all_merchants.view', labelKey: 'roles.extraViewAllMerchants' },
          { key: 'merchants.suspend.reset', labelKey: 'roles.extraUnsuspendMerchant' },
          { key: 'merchants.payment_methods.view', labelKey: 'roles.extraViewPaymentMethods' },
          { key: 'merchants.payment_methods.manage', labelKey: 'roles.extraManagePaymentMethods' },
          { key: 'merchants.api_disbursement.manage', labelKey: 'roles.extraManageApiDisbursement' },
        ],
      },
      {
        id: 'wh_delivery_own',
        labelKey: 'roles.featWhDeliveryOwn',
        appliesTo: 'merchant',
        actions: { view: 'webhook.delivery.view' },
        extras: [{ key: 'webhook.delivery.retry', labelKey: 'roles.extraRetry' }],
      },
      {
        id: 'wh_delivery_all',
        labelKey: 'roles.featWhDeliveryAll',
        appliesTo: 'internal',
        actions: { view: 'webhook.delivery.internal.view' },
        extras: [
          { key: 'webhook.delivery.internal.retry', labelKey: 'roles.extraRetry' },
          { key: 'webhook.delivery.internal.mark_dead', labelKey: 'roles.extraMarkDead' },
        ],
      },
    ],
  },
  {
    id: 'users_roles',
    labelKey: 'roles.catUsersRoles',
    hintKey: 'roles.catUsersRolesHint',
    color: 'grape',
    features: [
      {
        id: 'sub_users',
        labelKey: 'roles.featSubUsers',
        appliesTo: 'merchant',
        descriptionKey: 'roles.featSubUsersDesc',
        actions: { view: 'users.view', edit: 'users.manage' },
      },
      {
        id: 'internal_users',
        labelKey: 'roles.featInternalUsers',
        appliesTo: 'internal',
        descriptionKey: 'roles.featInternalUsersDesc',
        actions: {
          view: 'users.internal.view',
          edit: 'users.internal.manage',
        },
        extras: [{ key: 'users.suspend.reset', labelKey: 'roles.extraUnsuspendUser' }],
      },
      {
        // `users.mfa.reset` is module 'shared' — grantable to BOTH internal
        // roles (reset any user's MFA) and merchant roles (merchant_admin resets
        // MFA of sub-users in its own merchant). A dedicated `appliesTo: 'both'`
        // feature renders for either scope; isInScope keeps it in-bounds.
        id: 'user_mfa_reset',
        labelKey: 'roles.featResetMfa',
        descriptionKey: 'roles.featResetMfaDesc',
        appliesTo: 'both',
        actions: {},
        extras: [{ key: 'users.mfa.reset', labelKey: 'roles.extraResetUserMfa' }],
      },
      {
        id: 'roles',
        labelKey: 'roles.featRoles',
        appliesTo: 'internal',
        descriptionKey: 'roles.featRolesDesc',
        actions: {},
        extras: [
          { key: 'roles.merchant.manage', labelKey: 'roles.extraManageMerchantRole' },
          { key: 'roles.internal.manage', labelKey: 'roles.extraManageInternalRole' },
          { key: 'roles.delegate', labelKey: 'roles.extraDelegate' },
        ],
      },
    ],
  },
  {
    id: 'wallet',
    labelKey: 'roles.catWallet',
    hintKey: 'roles.catWalletHint',
    color: 'teal',
    features: [
      {
        id: 'wallet_own',
        labelKey: 'roles.featWalletOwn',
        appliesTo: 'merchant',
        actions: { view: 'wallet.view' },
        extras: [{ key: 'wallet.ledger.view', labelKey: 'roles.extraViewLedger' }],
      },
      {
        id: 'wallet_any',
        labelKey: 'roles.featWalletAny',
        appliesTo: 'internal',
        actions: { view: 'wallet.internal.view' },
        extras: [{ key: 'wallet.internal.adjust', labelKey: 'roles.extraAdjustAudited' }],
      },
    ],
  },
  {
    id: 'security',
    labelKey: 'roles.catSecurity',
    hintKey: 'roles.catSecurityHint',
    color: 'orange',
    features: [
      {
        id: 'api_credentials',
        labelKey: 'roles.featApiCredentials',
        appliesTo: 'merchant',
        actions: { view: 'account.credentials.view' },
        extras: [{ key: 'account.credentials.reveal', labelKey: 'roles.extraRevealToken' }],
      },
      {
        id: 'ipw_user',
        labelKey: 'roles.featIpwUser',
        appliesTo: 'both',
        actions: {
          view: 'ip_whitelist.user.view',
          edit: 'ip_whitelist.user.manage',
        },
      },
      {
        id: 'ipw_merchant',
        labelKey: 'roles.featIpwMerchant',
        appliesTo: 'merchant',
        actions: {
          view: 'ip_whitelist.merchant.view',
          edit: 'ip_whitelist.merchant.manage',
        },
      },
      {
        id: 'ipw_internal',
        labelKey: 'roles.featIpwInternal',
        appliesTo: 'internal',
        actions: {
          view: 'ip_whitelist.internal.view',
          edit: 'ip_whitelist.internal.manage',
        },
      },
    ],
  },
  {
    id: 'audit',
    labelKey: 'roles.catAudit',
    hintKey: 'roles.catAuditHint',
    color: 'red',
    features: [
      {
        id: 'audit_log',
        labelKey: 'roles.featAuditLog',
        appliesTo: 'internal',
        actions: { view: 'audit.view' },
      },
      {
        id: 'api_activity',
        labelKey: 'roles.featApiActivity',
        appliesTo: 'both',
        descriptionKey: 'roles.featApiActivityDesc',
        actions: { view: 'audit.api_log.view' },
        extras: [
          { key: 'audit.api_log.payok.view', labelKey: 'roles.extraViewPayokActivity' },
        ],
      },
      {
        id: 'settings',
        labelKey: 'roles.featSettings',
        appliesTo: 'internal',
        actions: {
          view: 'settings.view',
          edit: 'settings.manage',
        },
        extras: [
          { key: 'acquirers.manage', labelKey: 'roles.extraManageAcquirers' },
        ],
      },
    ],
  },
];

/**
 * Build a lookup of every permission key the catalog references, so we can
 * detect catalog drift if the seed grows a new permission we haven't slotted.
 */
function collectCatalogKeys(): Set<string> {
  const keys = new Set<string>();
  for (const cat of FEATURE_CATALOG) {
    for (const f of cat.features) {
      for (const k of Object.values(f.actions)) if (k) keys.add(k);
      for (const e of f.extras ?? []) keys.add(e.key);
    }
  }
  return keys;
}

export function RolesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const profile = useProfile();
  const { has, isSuperuser } = usePermissions();
  const [scopeFilter, setScopeFilter] = useState<RoleScope>('internal');
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Hard gate: only internal staff (or superuser) can manage roles. The server
  // already enforces this via requirePlatformUser; this short-circuit keeps the
  // page from rendering noisy API errors for merchant users who navigate here
  // directly. The sidebar already hides this link for them.
  if (!isPlatform(profile)) {
    return (
      <Stack gap="md">
        <Title order={2}>{t('roles.title')}</Title>
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {t('roles.internalOnlyAlert')}
        </Alert>
      </Stack>
    );
  }

  // Even an internal user needs at least one of the manage permissions to
  // see/edit anything meaningful. Superuser bypasses.
  const canManageAny =
    isSuperuser || has('roles.merchant.manage') || has('roles.internal.manage');
  if (!canManageAny) {
    return (
      <Stack gap="md">
        <Title order={2}>{t('roles.title')}</Title>
        <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
          {t('roles.noManagePermission', { permA: 'roles.internal.manage', permB: 'roles.merchant.manage' })}
        </Alert>
      </Stack>
    );
  }

  const rolesQuery = useQuery({
    queryKey: ['roles', scopeFilter],
    queryFn: () => rolesApi.list({ scope: scopeFilter }),
  });
  const permsQuery = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: () => rolesApi.listPermissions(),
  });

  // Auto-select first role when the scope changes.
  const rolesForScope = rolesQuery.data?.rows ?? [];
  if (selectedRoleId === null && rolesForScope.length > 0) {
    setSelectedRoleId(rolesForScope[0]?.id ?? null);
  }
  const selectedRole = rolesForScope.find((r) => r.id === selectedRoleId) ?? null;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('roles.title')}</Title>
          <Text c="dimmed" size="sm">
            {t('roles.subtitle')}
          </Text>
        </div>
        <Button leftSection={<IconPlus size={14} />} onClick={() => setCreateOpen(true)}>
          {t('roles.newRole')}
        </Button>
      </Group>

      <Tabs value={scopeFilter} onChange={(v) => { setScopeFilter((v as RoleScope) ?? 'internal'); setSelectedRoleId(null); }}>
        <Tabs.List>
          <Tabs.Tab value="internal">{t('roles.tabInternal')}</Tabs.Tab>
          <Tabs.Tab value="merchant">{t('roles.tabMerchant')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Grid>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card padding={0}>
            {rolesQuery.isLoading && (
              <Group justify="center" py="xl"><Loader /></Group>
            )}
            {rolesQuery.error && (
              <Alert color="red" icon={<IconAlertCircle size={16} />} m="md">
                {rolesQuery.error instanceof ApiCallError
                  ? `${rolesQuery.error.code}: ${rolesQuery.error.message}`
                  : t('common.error')}
              </Alert>
            )}
            {!rolesQuery.isLoading && !rolesQuery.error && (
              <Table>
                <Table.Tbody>
                  {rolesForScope.map((r) => (
                    <Table.Tr
                      key={r.id}
                      onClick={() => setSelectedRoleId(r.id)}
                      style={{
                        cursor: 'pointer',
                        background: r.id === selectedRoleId ? 'var(--mantine-color-blue-light)' : undefined,
                      }}
                    >
                      <Table.Td>
                        <Group gap={6}>
                          <Text fw={500}>{r.name}</Text>
                          {r.isSystem && (
                            <Badge size="xs" color="gray" variant="light" leftSection={<IconLock size={10} />}>
                              {t('roles.badgeSystem')}
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed" ff="monospace">{r.slug}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {rolesForScope.length === 0 && (
                    <Table.Tr><Table.Td>
                      <Text c="dimmed" ta="center" py="lg">{t('roles.empty')}</Text>
                    </Table.Td></Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            )}
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9 }}>
          {selectedRole ? (
            <RoleEditPanel
              // Remount per role so the panel's local `selected` state (and the
              // save-success alert) re-seed from the newly selected role's grants.
              // Without this, switching roles keeps the first role's checkboxes
              // because the `selected === null` init guard only fires once.
              key={selectedRole.id}
              role={selectedRole}
              allPermissions={permsQuery.data?.rows ?? []}
              loadingPermissions={permsQuery.isLoading}
              onChanged={() => {
                queryClient.invalidateQueries({ queryKey: ['roles', scopeFilter] });
              }}
            />
          ) : (
            <Card>
              <Text c="dimmed" ta="center" py="lg">{t('roles.selectPrompt')}</Text>
            </Card>
          )}
        </Grid.Col>
      </Grid>

      <CreateRoleModal
        opened={createOpen}
        scope={scopeFilter}
        onClose={() => setCreateOpen(false)}
        onCreated={(r) => {
          setCreateOpen(false);
          queryClient.invalidateQueries({ queryKey: ['roles'] });
          setSelectedRoleId(r.id);
        }}
      />
    </Stack>
  );
}

function RoleEditPanel({
  role,
  allPermissions,
  loadingPermissions,
  onChanged,
}: {
  role: RoleDto;
  allPermissions: readonly PermissionDto[];
  loadingPermissions: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const grantsQuery = useQuery({
    queryKey: ['role-permissions', role.id],
    queryFn: () => rolesApi.getRolePermissions(role.id),
  });
  const [selected, setSelected] = useState<Set<string> | null>(null);

  const initialKeys = grantsQuery.data?.permissionKeys;
  if (selected === null && initialKeys) setSelected(new Set(initialKeys));

  // Index of permission key -> module, so we can hide cells whose underlying
  // permission is out-of-scope for this role (e.g. merchant_admin shouldn't
  // see admin-only keys, internal_admin shouldn't see merchant-only keys).
  const moduleByKey = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of allPermissions) m.set(p.key, p.module);
    return m;
  }, [allPermissions]);
  const allowedModules = MODULES_BY_SCOPE[role.scope] ?? [];
  const isInScope = (key: string): boolean => {
    const mod = moduleByKey.get(key);
    return mod ? allowedModules.includes(mod) : false;
  };

  // Surface any permissions that the seed knows about but the catalog forgot
  // to slot — so we don't silently drop grant editing for a new key.
  const uncatalogedKeys = useMemo(() => {
    const catalog = collectCatalogKeys();
    return allPermissions
      .filter((p) => !catalog.has(p.key) && allowedModules.includes(p.module))
      .map((p) => p.key);
  }, [allPermissions, allowedModules]);

  const toggle = (key: string) => {
    setSelected((s) => {
      const next = new Set<string>(s ?? new Set<string>());
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const saveMutation = useMutation({
    meta: { successKey: 'notify.role.save.ok', errorKey: 'notify.role.save.fail' },
    // Send only in-scope keys. A role can carry grants whose module is out of
    // scope for its own scope (e.g. a merchant role that somehow holds an
    // `admin` permission); those are hidden from the matrix by `isInScope`, so
    // the user can't untick them — without this filter they'd be re-submitted
    // and the server would reject the whole save with `module_scope_mismatch`.
    // Filtering also lets a save quietly clean up such stray grants. Guard on a
    // loaded catalog: if `allPermissions` is empty (catalog failed to load) we
    // can't classify modules, so fall back to sending as-is rather than wiping.
    mutationFn: () => {
      const desired = [...(selected ?? [])];
      const payload = allPermissions.length > 0 ? desired.filter((k) => isInScope(k)) : desired;
      return rolesApi.setRolePermissions(role.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions', role.id] });
      onChanged();
    },
  });

  const dirty =
    selected !== null &&
    initialKeys !== undefined &&
    (selected.size !== initialKeys.length ||
      [...selected].some((k) => !initialKeys.includes(k)) ||
      initialKeys.some((k) => !selected.has(k)));

  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap={8}>
              <Title order={3}>{role.name}</Title>
              {role.isSystem && (
                <Badge color="gray" variant="light" leftSection={<IconLock size={12} />}>
                  {t('roles.badgeSystem')}
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed" ff="monospace" mt={2}>{role.slug}</Text>
            {role.description && <Text size="sm" mt={4}>{role.description}</Text>}
          </div>
          <Group gap="xs">
            <Button
              variant="default"
              disabled={!dirty}
              onClick={() => setSelected(new Set(initialKeys))}
            >
              {t('common.reset')}
            </Button>
            <Button loading={saveMutation.isPending} disabled={!dirty} onClick={() => saveMutation.mutate()}>
              {t('common.save')}
            </Button>
          </Group>
        </Group>

        {saveMutation.isSuccess && (
          <Alert color="green">
            {t('roles.savedSummary', {
              added: saveMutation.data.added,
              removed: saveMutation.data.removed,
              affected: saveMutation.data.affectedUsers,
            })}
          </Alert>
        )}

        {uncatalogedKeys.length > 0 && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            {t('roles.uncatalogedKeys', { keys: uncatalogedKeys.join(', ') })}
          </Alert>
        )}

        <Divider />

        {(loadingPermissions || grantsQuery.isLoading || selected === null) && (
          <Group justify="center" py="xl"><Loader /></Group>
        )}
        {selected !== null && !loadingPermissions && (
          <ScrollArea h={620} type="auto">
            <Stack gap="md">
              {FEATURE_CATALOG.map((cat) => (
                <CategorySection
                  key={cat.id}
                  category={cat}
                  selected={selected}
                  setSelected={setSelected}
                  isInScope={isInScope}
                  toggle={toggle}
                />
              ))}
            </Stack>
          </ScrollArea>
        )}
      </Stack>
    </Card>
  );
}

function CategorySection({
  category,
  selected,
  setSelected,
  isInScope,
  toggle,
}: {
  category: CategoryDef;
  selected: Set<string>;
  setSelected: (updater: (s: Set<string> | null) => Set<string>) => void;
  isInScope: (key: string) => boolean;
  toggle: (key: string) => void;
}) {
  const { t } = useTranslation();
  // Filter features down to what's actually in-scope for this role. A feature
  // with zero visible actions AND zero visible extras is hidden entirely.
  const visibleFeatures = useMemo(() => {
    return category.features
      .map((f) => {
        const actions: Partial<Record<FeatureAction, string>> = {};
        for (const col of ACTION_COLUMNS) {
          const k = f.actions[col.id];
          if (k && isInScope(k)) actions[col.id] = k;
        }
        const extras = (f.extras ?? []).filter((e) => isInScope(e.key));
        return { feature: f, actions, extras };
      })
      .filter((row) => Object.keys(row.actions).length > 0 || row.extras.length > 0);
  }, [category, isInScope]);

  if (visibleFeatures.length === 0) return null;

  // Aggregate keys for the "Grant all / Clear all" affordance.
  const allKeys: string[] = [];
  for (const row of visibleFeatures) {
    for (const k of Object.values(row.actions)) if (k) allKeys.push(k);
    for (const e of row.extras) allKeys.push(e.key);
  }
  const grantedCount = allKeys.filter((k) => selected.has(k)).length;
  const allOn = grantedCount === allKeys.length;

  const setAll = (next: boolean) => {
    setSelected((s) => {
      const out = new Set<string>(s ?? new Set<string>());
      for (const k of allKeys) {
        if (next) out.add(k);
        else out.delete(k);
      }
      return out;
    });
  };

  return (
    <Paper withBorder radius="md" p={0}>
      <Group
        justify="space-between"
        px="md"
        py="sm"
        style={{
          background:
            'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
          borderBottom:
            '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
        }}
      >
        <Group gap="xs">
          <Badge color={category.color} variant="filled" size="lg" radius="sm">
            {t(category.labelKey)}
          </Badge>
          <Text size="xs" c="dimmed">
            {t('roles.granted', { granted: grantedCount, total: allKeys.length })}
          </Text>
        </Group>
        <Button
          size="xs"
          variant="subtle"
          leftSection={allOn ? <IconCircleDashed size={14} /> : <IconCircleCheck size={14} />}
          onClick={() => setAll(!allOn)}
        >
          {allOn ? t('roles.clearAll') : t('roles.grantAll')}
        </Button>
      </Group>
      {category.hintKey && (
        <Text
          size="xs"
          c="dimmed"
          px="md"
          py={6}
          style={{
            background:
              'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))',
          }}
        >
          {t(category.hintKey)}
        </Text>
      )}
      <FeatureGrid
        visibleFeatures={visibleFeatures}
        selected={selected}
        toggle={toggle}
        categoryColor={category.color}
      />
    </Paper>
  );
}

/**
 * Fixed-width CSS Grid for the feature/CRUD matrix. Each row is its own
 * grid with identical column template — that way we can stripe rows by
 * just setting `background` on the row wrapper, no `display: contents`
 * cloning shenanigans needed. The action columns use fixed pixel widths
 * so the checkboxes line up across rows; the Fitur and Extras columns
 * are flexible so labels wrap rather than overflow. If the editor panel
 * shrinks below ~720px we let the row horizontally scroll inside its
 * container instead of breaking the grid.
 */
const GRID_COLS = 'minmax(220px, 1.6fr) 80px 80px 80px 110px minmax(200px, 2fr)';
const ROW_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: GRID_COLS,
  minWidth: 720,
  alignItems: 'start',
};
const CELL_PAD = '10px 14px';
const CELL_BORDER =
  '1px solid light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-4))';

function FeatureGrid({
  visibleFeatures,
  selected,
  toggle,
  categoryColor,
}: {
  visibleFeatures: ReadonlyArray<{
    feature: FeatureDef;
    actions: Partial<Record<FeatureAction, string>>;
    extras: readonly FeatureExtra[];
  }>;
  selected: Set<string>;
  toggle: (key: string) => void;
  categoryColor: string;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ overflowX: 'auto' }}>
      {/* Header row */}
      <div style={{ ...ROW_STYLE, borderBottom: CELL_BORDER }}>
        <div style={{ padding: CELL_PAD }}>
          <Text size="xs" fw={700} c="dimmed">{t('roles.colFeature')}</Text>
        </div>
        {ACTION_COLUMNS.map((col) => (
          <div key={col.id} style={{ padding: CELL_PAD, textAlign: 'center' }}>
            <Text size="xs" fw={700} c={col.color}>
              {t(col.labelKey).toUpperCase()}
            </Text>
          </div>
        ))}
        <div style={{ padding: CELL_PAD }}>
          <Text size="xs" fw={700} c="dimmed">{t('roles.colExtras')}</Text>
        </div>
      </div>

      {/* Data rows */}
      {visibleFeatures.map(({ feature, actions, extras }, rowIdx) => {
        const scopeMeta = APPLIES_TO_META[feature.appliesTo];
        const rowBg =
          rowIdx % 2 === 1
            ? 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))'
            : 'transparent';
        return (
          <div
            key={feature.id}
            style={{
              ...ROW_STYLE,
              background: rowBg,
              borderBottom:
                rowIdx === visibleFeatures.length - 1 ? 'none' : CELL_BORDER,
            }}
          >
            <div style={{ padding: CELL_PAD }}>
              <Group gap={6} mb={2} wrap="wrap">
                <Text size="sm" fw={500}>{t(feature.labelKey)}</Text>
                <Tooltip
                  label={
                    feature.appliesTo === 'internal'
                      ? t('roles.tooltipInternal')
                      : feature.appliesTo === 'merchant'
                      ? t('roles.tooltipMerchant')
                      : t('roles.tooltipBoth')
                  }
                  withArrow
                >
                  <Badge size="xs" color={scopeMeta.color} variant="light" radius="sm">
                    {t(scopeMeta.labelKey)}
                  </Badge>
                </Tooltip>
              </Group>
              {feature.descriptionKey && (
                <Text size="xs" c="dimmed" mt={2}>
                  {t(feature.descriptionKey)}
                </Text>
              )}
            </div>
            {ACTION_COLUMNS.map((col) => {
              const key = actions[col.id];
              return (
                <div
                  key={col.id}
                  style={{
                    padding: CELL_PAD,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {key ? (
                    <Tooltip label={key} withArrow openDelay={300}>
                      <Checkbox
                        checked={selected.has(key)}
                        onChange={() => toggle(key)}
                        color={col.color}
                        size="md"
                        aria-label={`${t(feature.labelKey)} — ${t(col.labelKey)}`}
                      />
                    </Tooltip>
                  ) : (
                    <Text c="dimmed" size="sm">—</Text>
                  )}
                </div>
              );
            })}
            <div style={{ padding: CELL_PAD }}>
              {extras.length > 0 ? (
                <Group gap="xs" wrap="wrap">
                  {extras.map((e) => (
                    <Tooltip key={e.key} label={e.key} withArrow openDelay={300}>
                      <Checkbox
                        checked={selected.has(e.key)}
                        onChange={() => toggle(e.key)}
                        label={t(e.labelKey)}
                        color={categoryColor}
                        size="xs"
                      />
                    </Tooltip>
                  ))}
                </Group>
              ) : (
                <Text c="dimmed" size="sm">—</Text>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CreateRoleModal({
  opened,
  scope,
  onClose,
  onCreated,
}: {
  opened: boolean;
  scope: RoleScope;
  onClose: () => void;
  onCreated: (r: RoleDto) => void;
}) {
  const { t } = useTranslation();
  const form = useForm({
    initialValues: { name: '', slug: '', description: '', scope },
    validate: {
      name: (v) => (v.trim().length === 0 ? 'name required' : null),
      slug: (v) =>
        /^[a-z0-9_]+$/.test(v) ? null : 'slug must be lower_snake_case (a-z, 0-9, underscore)',
    },
  });
  const mutation = useMutation({
    meta: { successKey: 'notify.role.create.ok', errorKey: 'notify.role.create.fail' },
    mutationFn: () =>
      rolesApi.create({
        name: form.values.name.trim(),
        slug: form.values.slug.trim(),
        description: form.values.description.trim() || undefined,
        scope: form.values.scope,
      }),
    onSuccess: (r) => {
      form.reset();
      onCreated(r);
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title={t('roles.createTitle')} size="md">
      <form onSubmit={form.onSubmit(() => mutation.mutate())}>
        <Stack gap="sm">
          <TextInput label={t('roles.nameLabel')} required {...form.getInputProps('name')} />
          <TextInput
            label={t('roles.slugLabel')}
            description={t('roles.slugDesc')}
            required
            {...form.getInputProps('slug')}
          />
          <Select
            label={t('roles.scopeLabel')}
            data={[{ value: 'internal', label: t('auth.internal') }, { value: 'merchant', label: t('auth.merchant') }]}
            {...form.getInputProps('scope')}
          />
          <Textarea label={t('roles.descriptionLabel')} {...form.getInputProps('description')} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} type="button">{t('common.cancel')}</Button>
            <Button type="submit" loading={mutation.isPending}>{t('common.create')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
